import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  VERWANDTSCHAFT_LABEL,
  ROLLE_DER_BESTEHENDEN,
  ROLLE_DER_NEUEN,
  formatDatum,
  datumAusSpalten,
  EREIGNIS_LABEL,
  ereignisrolleText,
  markdownErsteZeile,
  type Verwandtschaftsart,
  type Ereignisrolle,
} from "@stammbaum/shared";
import { api, bildUrl, istPdf } from "../api";
import { usePersonKarte } from "../komponenten/PersonKarte";
import { Markdown } from "../komponenten/Markdown";
import { useTitel, personTitel } from "../titel";
import { umfeldKarte } from "../komponenten/umfeld";
import { PersonSucher } from "../komponenten/PersonSucher";
import { FotoAblage, type Bilanz } from "../komponenten/FotoAblage";
import { Verwandtschaft } from "../komponenten/Verwandtschaft";
import {
  PersonFormular,
  werteAusPerson,
  werteFuerApi,
  LEERE_PERSON,
  type PersonWerte,
} from "../komponenten/PersonFormular";
import {
  EreignisFormular,
  neuesEreignis,
  ereignisAlsWerte,
  type EreignisWerte,
} from "../komponenten/EreignisFormular";

type Modus =
  | { art: "ansicht" }
  | { art: "bearbeiten" }
  /**
   * In welche Familie es geht, steht in `zielFamilie`: entweder die
   * angeklickte Zeile oder `null` fuer eine neue Familie. Eine Auswahl
   * im Formular gibt es nicht mehr - sie waere nur eine Gelegenheit,
   * sich zu verklicken.
   */
  | { art: "verwandt"; was: Verwandtschaftsart };

const ARTEN: Verwandtschaftsart[] = ["elternteil", "geschwister", "partner", "kind"];

/**
 * Mit diesem Umkreis wird die Verwandtschaft geladen. Steht als Konstante
 * da, weil der Wert an drei Stellen gebraucht wird: in der Abfrage, in
 * ihrem Schluessel und in der Infokarte, die daraus ableitet, wessen
 * Familie vollstaendig geladen sein kann.
 */
const BAUM_GRAD = 4;

/** Stabile Leerwerte - sonst rechnen die Memos bei jedem Rendern neu. */
const KEINE_PERSONEN: any[] = [];
const KEINE_KANTEN: any[] = [];

/**
 * Welche Verwandtschaft entsteht, wenn in dieser Zeile dieser Familie
 * jemand dazukommt?
 *
 * Das folgt aus zwei Angaben, die beide schon dastehen: der eigenen Rolle
 * in der Familie und der Zeile. Wer hier Kind ist, bekommt in der
 * Elternzeile einen Elternteil und in der Kinderzeile ein Geschwister; wer
 * Elternteil ist, in der Elternzeile einen Partner und in der Kinderzeile
 * ein Kind. Deshalb braucht es keinen eigenen Abschnitt "Verwandte
 * ergaenzen" mehr - die Frage "wie steht der zu mir?" ist an der Stelle
 * bereits beantwortet.
 */
function artFuerZeile(
  eigeneRolle: "elternteil" | "kind",
  zeile: "elternteil" | "kind",
): Verwandtschaftsart {
  if (eigeneRolle === "kind") return zeile === "elternteil" ? "elternteil" : "geschwister";
  return zeile === "elternteil" ? "partner" : "kind";
}

/** In dieser Reihenfolge stehen die Beteiligten in der Ereignisliste. */
const ROLLENFOLGE: Ereignisrolle[] = [
  "hauptperson",
  "partner",
  "elternteil",
  "kind",
  "pate",
  "trauzeuge",
  "beteiligt",
];

export default function PersonSeite() {
  const { id } = useParams();
  const personId = Number(id);
  const navigate = useNavigate();
  const client = useQueryClient();

  const [modus, setModus] = useState<Modus>({ art: "ansicht" });
  const [zielFamilie, setZielFamilie] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /** Offene Ereignismaske: neu oder ein vorhandenes Ereignis. */
  const [ereignisWerte, setEreignisWerte] = useState<EreignisWerte | null>(null);
  /**
   * Welche Ereignisse ihre Anmerkung zeigen.
   *
   * Zugeklappt als Vorgabe: in einer Anmerkung steht jetzt auch einmal ein
   * ganzer Matrikeneintrag, und zwanzig davon machten aus der Liste eine
   * Textwand.
   */
  const [offeneTexte, setOffeneTexte] = useState<Set<number>>(new Set());
  /** Familie, deren Bezeichnung gerade bearbeitet wird. */
  const [umbenennen, setUmbenennen] = useState<{ id: number; text: string } | null>(null);
  /** Offene Suche, um eine vorhandene Person in eine Familie zu hängen. */
  const [einhaengen, setEinhaengen] = useState<{
    familieId: number;
    rolle: "elternteil" | "kind";
  } | null>(null);

  /*
   * Beim Wechsel auf eine andere Person alles Offene schliessen.
   *
   * React verwendet dieselbe Komponente weiter, wenn sich nur die ID in der
   * Adresse aendert - eine offene Personensuche oder eine halb ausgefuellte
   * Ereignismaske stuenden sonst ploetzlich bei jemand anderem.
   */
  useEffect(() => {
    setModus({ art: "ansicht" });
    setEreignisWerte(null);
    setEinhaengen(null);
    setUmbenennen(null);
    setZielFamilie(null);
    setOffeneTexte(new Set());
    setFehler(null);
  }, [personId]);

  const detail = useQuery({ queryKey: ["person", personId], queryFn: () => api.person(personId) });

  /*
   * Vor den fruehen Returns, damit auch die Fehlerseite einen Titel hat -
   * und damit die Reihenfolge der Hooks gleich bleibt.
   */
  useTitel(personTitel(detail.data?.person, personId));
  /**
   * Grad 4, nicht 3: ein Cousin liegt vier Blutsschritte weg (hinauf zum
   * Elternteil, hinauf zum Grosselternteil, hinunter zum Onkel, hinunter
   * zum Cousin). Mit 3 fielen genau sie heraus, obwohl Onkel und Tante
   * dastanden - Urgrosseltern und Urenkel dagegen waren mit 3 schon dabei.
   * Neu sind Cousins, Grossonkel und -tanten, Grossnichten und -neffen
   * sowie Ururgrosseltern und Ururenkel. Die Gruppierung in
   * `Verwandtschaft` faengt die Menge auf.
   *
   * Der Grad gehoert in den Schluessel: sonst liefert der
   * Zwischenspeicher nach einer Aenderung stillschweigend den alten
   * Umfang.
   */
  const baum = useQuery({
    queryKey: ["baum", personId, BAUM_GRAD],
    queryFn: () => api.stammbaum(personId, BAUM_GRAD),
  });

  /*
   * Die Infokarte, einmal fuer die ganze Seite. Sie lebt von der
   * Baumantwort, die ohnehin geladen wird: dort stehen beide Daten
   * vollstaendig, Bezeichnung, Abstand und das Portrait, und aus den
   * Kanten rechnet `umfeldKarte` Eltern, Partner und Kinderzahl aus.
   * Deshalb kein einziger zusaetzlicher Aufruf.
   */
  const baumLeute: any[] = baum.data?.personen ?? KEINE_PERSONEN;
  const baumKanten: any[] = baum.data?.kanten ?? KEINE_KANTEN;
  const baumPersonen = useMemo(() => new Map(baumLeute.map((p: any) => [p.id, p])), [baumLeute]);
  const umfeld = useMemo(
    () => umfeldKarte({ personen: baumLeute, kanten: baumKanten }, BAUM_GRAD),
    [baumLeute, baumKanten],
  );
  const namen = useMemo(() => {
    const m = new Map(baumLeute.map((p: any) => [p.id, `${p.vorname} ${p.famname}`.trim()]));
    return (id: number) => m.get(id) ?? null;
  }, [baumLeute]);
  const karte = usePersonKarte(namen);

  /**
   * Die Angaben zu einer Person aus beiden Quellen zusammenlegen.
   *
   * Der Familienblock liefert den ganzen gefilterten Datensatz, die
   * Baumantwort zusaetzlich Bezeichnung, Abstand und Portrait. Wer in der
   * Baumantwort fehlt - ein Trauzeuge ohne Verwandtschaft -, behaelt
   * einfach das, was er hat.
   */
  const fuerKarte = (x: any) => ({ ...x, ...(baumPersonen.get(x.id) ?? {}) });

  function neuLaden() {
    client.invalidateQueries({ queryKey: ["person"] });
    client.invalidateQueries({ queryKey: ["baum"] });
    client.invalidateQueries({ queryKey: ["personen"] });
    // Ein neu eingetippter Ort soll beim nächsten Ereignis vorgeschlagen werden.
    client.invalidateQueries({ queryKey: ["orte"] });
    // Die Bildseite zeigt "Gehört zu" - das ändert sich hier mit.
    client.invalidateQueries({ queryKey: ["medium"] });
    client.invalidateQueries({ queryKey: ["medienZiele"] });
  }

  if (detail.error) return <p className="text-red-700">{(detail.error as Error).message}</p>;
  if (!detail.data) return <p className="text-black/40">lädt …</p>;

  const p = detail.data.person;
  const familien: any[] = detail.data.familien ?? [];
  const darfBearbeiten: boolean = detail.data.darfBearbeiten;
  /**
   * Ändern und Löschen sind zwei verschiedene Rechte: ein Bearbeiter darf
   * alles berichtigen, was er sieht, aber nur löschen, was er selbst
   * angelegt hat. Ohne diese Unterscheidung stünde hier ein Knopf, der
   * nur 403 liefert.
   */
  const darfLoeschen: boolean = detail.data.darfLoeschen ?? false;

  // Über datumAusSpalten, damit auch "vor 1900" ankommt - dort ist `von`
  // leer und nur `bis` gesetzt.
  const datum = (art: "geburt" | "tod") => {
    const d = datumAusSpalten(p, art);
    return d ? formatDatum(d) : null;
  };

  // ---- Speichern -----------------------------------------------------------

  async function personSpeichern(w: PersonWerte) {
    setFehler(null);
    setLaeuft(true);
    try {
      await api.personAendern(personId, werteFuerApi(w));
      neuLaden();
      setModus({ art: "ansicht" });
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Speichern fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  async function verwandtenSpeichern(w: PersonWerte, was: Verwandtschaftsart) {
    setFehler(null);
    setLaeuft(true);
    try {
      const ergebnis = await api.verwandtenAnlegen({
        ausgehendVon: personId,
        art: was,
        familieId: zielFamilie,
        person: werteFuerApi(w),
      });
      neuLaden();
      setModus({ art: "ansicht" });
      navigate(`/person/${ergebnis.person.id}`);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Anlegen fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  /**
   * Ein Stapel ist hochgeladen.
   *
   * Bei genau einem neuen Bild geht es weiter auf dessen Seite — das ist
   * fast immer der nächste Schritt (Datum, Ort, wer ist drauf). Bei
   * mehreren nicht: wer sieben Bilder gezogen hat, will sie erst einmal
   * alle sehen und nicht auf dem siebten landen.
   */
  function stapelFertig(b: Bilanz) {
    neuLaden();

    const meldungen = [
      b.schonDa > 0 &&
        `${b.schonDa} ${b.schonDa === 1 ? "Bild war" : "Bilder waren"} schon vorhanden und ` +
          `${b.schonDa === 1 ? "wurde" : "wurden"} nur zugeordnet`,
      ...b.fehler,
    ].filter(Boolean) as string[];
    setFehler(meldungen.length > 0 ? meldungen.join(" · ") : null);

    const ersteId = b.ids[0];
    if (b.ids.length === 1 && ersteId !== undefined && meldungen.length === 0) {
      navigate(`/foto/${ersteId}`);
    }
  }

  /** Dieses Bild (bzw. den Ausschnitt) zum Portrait machen. */
  async function alsPortrait(medienId: number, tagId: number | null) {
    setFehler(null);
    try {
      await api.portraitSetzen(personId, medienId, tagId);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Konnte Portrait nicht setzen.");
    }
  }

  /** Dieses Bild gehört nicht zu dieser Person. */
  async function fotoZuordnungLoesen(medienId: number) {
    if (
      !confirm(
        `Dieses Bild gehört nicht zu ${p.vorname} ${p.famname}?\n\n` +
          "Die Zuordnung wird gelöst, ebenso Markierungen dieser Person auf " +
          "dem Bild und ein Portrait, das darauf zeigt.\n" +
          "Das Bild selbst bleibt erhalten.",
      )
    )
      return;
    setFehler(null);
    try {
      const r = await api.zuordnungLoesen(medienId, "person", personId);
      neuLaden();
      if (r.entferntesPortrait) {
        setFehler("Zuordnung gelöst. Das war das Portrait — bitte ein neues wählen.");
      }
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Lösen fehlgeschlagen.");
    }
  }

  /** Die Anmerkung eines Ereignisses auf- oder zuklappen. */
  function textUmschalten(id: number) {
    setOffeneTexte((alt) => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  }

  /** Ereignis entfernen. Bei Geburt und Tod faellt auch das Datum der Person weg. */
  async function ereignisLoeschen(e: any) {
    const bezeichnung = EREIGNIS_LABEL[e.art as keyof typeof EREIGNIS_LABEL] ?? e.art;
    const zusatz =
      e.art === "geburt" || e.art === "tod"
        ? "\n\nDamit wird auch das Datum bei der Person gelöscht."
        : "";
    if (!confirm(`${bezeichnung} löschen?${zusatz}`)) return;
    setFehler(null);
    try {
      await api.ereignisLoeschen(e.id);
      setEreignisWerte(null);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Löschen fehlgeschlagen.");
    }
  }

  /** Bezeichnung der Familie speichern - leer heisst "ohne Bezeichnung". */
  async function familieUmbenennen() {
    if (!umbenennen) return;
    const { id, text } = umbenennen;
    setUmbenennen(null);
    setFehler(null);
    try {
      await api.familieAendern(id, { bezeichnung: text.trim() || null });
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Umbenennen fehlgeschlagen.");
    }
  }

  /**
   * Eine weitere Familie anlegen, in der diese Person Elternteil ist.
   *
   * Der Weg zur zweiten Ehe. Danach steht in der Elternzeile der neuen,
   * noch leeren Familie „+ Partner“ - und erst dort entscheidet sich,
   * ob der Partner gesucht oder angelegt wird. Vorher ging hier die
   * Suche gleich mit auf; sie blieb aber unsichtbar, sobald der
   * Benutzer bestehende Personen nicht verbinden darf.
   */
  async function weitereFamilie() {
    setFehler(null);
    // Gesperrt, solange der Aufruf laeuft: zwei Klicks hintereinander
    // legen sonst zwei Familien an, und aufraeumen laesst sich das nicht
    // von selbst - leer sind sie ja nicht.
    setLaeuft(true);
    try {
      await api.familieAnlegen({ personId, rolle: "elternteil" });
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Anlegen fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  /**
   * Eine neue Person anlegen und als Verwandte einhängen.
   *
   * `familieId === null` heisst: eine neue Familie entsteht dabei. Das ist
   * der Einstieg für jemanden, der noch zu keiner Familie gehört.
   */
  function neuVerwandten(familieId: number | null, was: Verwandtschaftsart) {
    setFehler(null);
    setEinhaengen(null);
    setZielFamilie(familieId);
    setModus({ art: "verwandt", was });
  }

  /** Eine schon vorhandene Person zusätzlich in diese Familie hängen. */
  async function mitgliedHinzufuegen(
    familieId: number,
    rolle: "elternteil" | "kind",
    person: { id: number },
  ) {
    setFehler(null);
    try {
      await api.mitgliedHinzufuegen(familieId, { personId: person.id, rolle });
      // Erst bei Erfolg schliessen. Ob eine bestimmte Person eingehängt
      // werden darf, hängt an IHREM Ersteller und steht damit erst nach
      // der Wahl fest - fällt die Lade bei einer Absage sofort zu, sieht
      // es aus, als hätte der Klick nichts getan.
      setEinhaengen(null);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Hinzufügen fehlgeschlagen.");
    }
  }

  /**
   * Die Person ganz entfernen. Für Dubletten aus dem Import gedacht —
   * deshalb steht im Dialog, was mit verschwindet.
   */
  async function personLoeschen() {
    const anzahl = {
      familien: familien.length,
      ereignisse: detail.data.ereignisse?.length ?? 0,
      fotos: detail.data.medien?.length ?? 0,
    };
    const text =
      `${p.vorname} ${p.famname} wirklich löschen?\n\n` +
      `Damit verschwinden: die Zugehörigkeit zu ${anzahl.familien} Familie(n), ` +
      `${anzahl.ereignisse} Ereignis(se) und ${anzahl.fotos} Bildzuordnung(en), ` +
      `dazu Markierungen und Portrait.\n\n` +
      "Die Bilder selbst bleiben, ebenso Ereignisse, an denen noch jemand " +
      "anderer beteiligt ist.\n\nDas lässt sich nicht rückgängig machen.";
    if (!confirm(text)) return;

    setFehler(null);
    setLaeuft(true);
    try {
      await api.personLoeschen(personId);
      // Erst aus dem Zwischenspeicher nehmen, dann neu laden: sonst holt
      // React Query die eben geloeschte Person sofort wieder - und bekommt
      // erwartungsgemaess einen Fehler.
      client.removeQueries({ queryKey: ["person", personId] });
      client.removeQueries({ queryKey: ["baum", personId] });
      neuLaden();
      navigate("/");
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Löschen fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  async function loesen(familieId: number, wenId: number) {
    if (!confirm("Zugehörigkeit zu dieser Familie lösen? Die Person selbst bleibt bestehen.")) return;
    try {
      await api.mitgliedLoesen(familieId, wenId);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Lösen fehlgeschlagen.");
    }
  }

  // ---- Formulare -----------------------------------------------------------

  if (modus.art === "bearbeiten") {
    return (
      <div>
        <h1 className="mb-6 text-xl font-semibold tracking-tight">
          {p.famname} {p.vorname} bearbeiten
        </h1>
        <PersonFormular
          start={werteAusPerson(p, detail.data.namen)}
          knopftext="Änderungen speichern"
          fehler={fehler}
          laeuft={laeuft}
          onSpeichern={personSpeichern}
          onAbbrechen={() => setModus({ art: "ansicht" })}
        />

        {darfLoeschen && (
          <div className="mt-10 border-t border-black/10 pt-4 dark:border-white/10">
            <button
              onClick={personLoeschen}
              disabled={laeuft}
              className="text-sm text-black/40 underline hover:text-red-700 disabled:opacity-50
                         dark:text-white/40"
            >
              Diese Person löschen
            </button>
            <p className="mt-1 text-xs text-black/40 dark:text-white/40">
              Für Dubletten aus dem Import. Bilder und Ereignisse mit anderen
              Beteiligten bleiben erhalten.
            </p>
          </div>
        )}
      </div>
    );
  }

  if (modus.art === "verwandt") {
    const was = modus.was;
    // Die Familie, in die es geht - `null` heisst "eine neue".
    const ziel = familien.find((f) => f.id === zielFamilie);

    // Der neue Familienname ist meistens derselbe - außer bei Partnern.
    const start: PersonWerte = {
      ...LEERE_PERSON,
      famname: was === "partner" ? "" : p.famname ?? "",
    };

    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight">
          {VERWANDTSCHAFT_LABEL[was]} von {p.vorname} {p.famname} anlegen
        </h1>

        {/*
          Eine Auswahlliste gibt es hier nicht mehr: entweder kam der Weg
          aus einer Familienzeile - dann steht die Familie fest - oder er
          kam aus der Liste der fehlenden Verwandtschaften, und dann gibt
          es bewusst keine passende.
        */}
        {zielFamilie === null ? (
          <p className="my-5 rounded-lg border border-black/10 p-4 text-sm text-black/60
                        dark:border-white/10 dark:text-white/60">
            Dafür entsteht eine neue Familie um {p.vorname}
            {was === "elternteil" || was === "geschwister" ? " als Kind" : " als Elternteil"}.
          </p>
        ) : (
          <p className="my-5 rounded-lg border border-black/10 p-4 text-sm text-black/60
                        dark:border-white/10 dark:text-white/60">
            kommt in die Familie{" "}
            <span className="font-medium text-black dark:text-white">
              {ziel ? ziel.bezeichnung || "ohne Bezeichnung" : "die gewählte"}
            </span>
            {ziel && (
              <>
                {" "}
                (
                {ziel.mitglieder
                  .map((m: any) => `${m.person.vorname} ${m.person.famname}`)
                  .join(", ")}
                )
              </>
            )}
          </p>
        )}

        <PersonFormular
          start={start}
          knopftext={`${VERWANDTSCHAFT_LABEL[was]} anlegen`}
          fehler={fehler}
          laeuft={laeuft}
          onSpeichern={(w) => verwandtenSpeichern(w, was)}
          onAbbrechen={() => setModus({ art: "ansicht" })}
        />
      </div>
    );
  }

  // ---- Ansicht -------------------------------------------------------------

  /**
   * Verwandtschaften, fuer die keine der Familien taugt.
   *
   * Eine Familie gibt der Person genau eine Rolle: Kind oder Elternteil.
   * Wer nur Elternteil ist, hat keine Elternzeile, in der "+ Elternteil"
   * stehen koennte - und ohne diese Liste waere der eigene Vater nicht
   * mehr eintragbar. Fuer diese Arten entsteht eine neue Familie.
   *
   * "Taugt" heisst auch: dort ist noch Platz. Zwei Elternteile sind das
   * Maximum, und wer Kind einer vollstaendigen Familie ist, braucht
   * trotzdem einen Weg zu Stief- oder Adoptiveltern - der geht ueber
   * eine zweite Familie, in der er Kind ist. Genau komplementaer zu den
   * Zeilenknoepfen: was dort steht, fehlt hier, und umgekehrt. Sonst
   * fuehrten zwei Wege zum selben Ziel und einer davon in einen Fehler.
   */
  const taugt = (a: Verwandtschaftsart) =>
    familien.some(
      (f) =>
        f.eigeneRolle === ROLLE_DER_BESTEHENDEN[a] &&
        !(ROLLE_DER_NEUEN[a] === "elternteil" && (f.elternAnzahl ?? 0) >= 2),
    );
  const fehlendeArten = ARTEN.filter((a) => !taugt(a));

  /**
   * Bild im Kopf: das ausgewählte Portrait, sonst hilfsweise das erste Foto.
   * Ohne diese Unterscheidung zeigt der Kopf ein Gruppenbild, obwohl längst
   * ein Ausschnitt als Portrait hinterlegt ist.
   */
  const portrait = detail.data.portrait as { medienId: number; tagId: number | null } | null;
  const erstesFoto = detail.data.medien?.[0];
  const kopfbild = portrait
    ? { medienId: portrait.medienId, tagId: portrait.tagId }
    : erstesFoto
      ? { medienId: erstesFoto.m.id, tagId: erstesFoto.tagId as number | null }
      : null;

  return (
    <div>
      <div className="flex flex-wrap items-start gap-5">
        {kopfbild && (
          <Link to={`/foto/${kopfbild.medienId}`} title="zum Bild">
            <img
              src={bildUrl(kopfbild.medienId, 480, kopfbild.tagId)}
              alt=""
              className="h-40 w-40 rounded-xl object-cover shadow"
            />
          </Link>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {p.famname} {p.vorname}
          </h1>
          {p.rufname && (
            <p className="text-black/60 dark:text-white/60">genannt {p.rufname}</p>
          )}
          {p.maedchenname && (
            <p className="text-black/60 dark:text-white/60">geborene {p.maedchenname}</p>
          )}
          {/*
            Abweichende Namen aus Quellen. Die Fundstelle steht im
            Hinweistext statt in der Zeile — unter dem Namen ist kein Platz
            für „Taufbuch Echsenbach 1834, fol. 22", gebraucht wird sie
            trotzdem, sobald jemand nachfragt, woher das kommt.
          */}
          {(detail.data.namen ?? []).length > 0 && (
            <p className="text-black/60 dark:text-white/60">
              auch{" "}
              {(detail.data.namen ?? []).map((n: any, i: number) => (
                <span key={n.id}>
                  {i > 0 && ", "}
                  <span title={n.notiz ?? undefined} className={n.notiz ? "underline decoration-dotted" : ""}>
                    {[n.vorname, n.famname].filter(Boolean).join(" ")}
                  </span>
                  {n.art === "angenommen" && (
                    <span className="text-black/40 dark:text-white/40"> (angenommen)</span>
                  )}
                </span>
              ))}
            </p>
          )}
          <p className="mt-1 text-black/70 dark:text-white/70">
            {(datum("geburt") || p.geburtsortName) && (
              <>
                * {datum("geburt")}
                {p.geburtsortName && (
                  // Kurz angezeigt, der ganze Pfad steht im Hinweistext.
                  <span title={p.geburtsortPfad ?? undefined}>
                    {datum("geburt") ? " " : ""}in {p.geburtsortName}
                  </span>
                )}
              </>
            )}
            {datum("tod") && <> &nbsp;† {datum("tod")}</>}
          </p>
          {p.beruf && <p className="mt-1 text-sm">{p.beruf}</p>}
          {p.wohnortName && (
            <p
              className="text-sm text-black/60 dark:text-white/60"
              title={p.wohnortPfad ?? undefined}
            >
              {p.wohnortName}
            </p>
          )}
          {p.kurzinfo && <p className="mt-2 text-sm">{p.kurzinfo}</p>}
        </div>

        <div className="flex gap-2">
          <Link
            to={`/baum/${personId}`}
            className="rounded-lg border border-black/15 px-3 py-1.5 text-sm
                       hover:border-black/40 dark:border-white/15"
          >
            Stammbaum
          </Link>
          {darfBearbeiten && (
            <button
              onClick={() => setModus({ art: "bearbeiten" })}
              className="rounded-lg border border-black/15 px-3 py-1.5 text-sm
                         hover:border-black/40 dark:border-white/15"
            >
              Bearbeiten
            </button>
          )}
        </div>
      </div>

      {p.notiz && (
        <div className="mt-5 rounded-lg bg-black/[0.03] p-4 text-sm dark:bg-white/5">
          <Markdown text={p.notiz} />
        </div>
      )}

      {fehler && (
        <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {fehler}
        </p>
      )}

      {(familien.length > 0 || darfBearbeiten) && (
        <section className="mt-8">
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-black/50">
              Familien
            </h2>
            {darfBearbeiten && (
              <button
                onClick={weitereFamilie}
                disabled={laeuft}
                title="für eine zweite Ehe: neue Familie mit dieser Person als Elternteil"
                className="rounded-lg border border-black/15 px-3 py-1 text-sm
                           hover:border-black/40 dark:border-white/15"
              >
                + weitere Familie
              </button>
            )}
          </div>
          {familien.length === 0 && !darfBearbeiten && (
            <p className="text-sm text-black/50">Noch keiner Familie zugeordnet.</p>
          )}

          <div className="space-y-3">
            {familien.map((f) => (
              <div key={f.id} className="rounded-lg border border-black/10 p-3 dark:border-white/10">
                <p className="mb-2 flex flex-wrap items-baseline gap-2 text-sm font-medium">
                  {umbenennen && umbenennen.id === f.id ? (
                    <>
                      <input
                        autoFocus
                        value={umbenennen.text}
                        onChange={(e) => setUmbenennen({ id: f.id, text: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") familieUmbenennen();
                          if (e.key === "Escape") setUmbenennen(null);
                        }}
                        maxLength={120}
                        placeholder="z.B. Huber–Mayr, zweite Ehe"
                        className="rounded border border-black/15 px-2 py-0.5 font-normal
                                   outline-none focus:border-black/40 dark:border-white/15
                                   dark:bg-neutral-800"
                      />
                      <button
                        onClick={familieUmbenennen}
                        className="rounded border border-black/15 px-2 py-0.5 text-xs font-normal
                                   hover:border-black/40 dark:border-white/15"
                      >
                        übernehmen
                      </button>
                      <button
                        onClick={() => setUmbenennen(null)}
                        className="text-xs font-normal text-black/40 hover:text-black
                                   dark:text-white/40"
                      >
                        abbrechen
                      </button>
                    </>
                  ) : (
                    <>
                      {darfBearbeiten ? (
                        <button
                          onClick={() => setUmbenennen({ id: f.id, text: f.bezeichnung ?? "" })}
                          title="Bezeichnung ändern"
                          className="rounded px-1 -mx-1 text-left hover:bg-black/5 dark:hover:bg-white/10"
                        >
                          {f.bezeichnung || (
                            <span className="font-normal text-black/40 dark:text-white/40">
                              ohne Bezeichnung ✎
                            </span>
                          )}
                        </button>
                      ) : (
                        <span>{f.bezeichnung || "ohne Bezeichnung"}</span>
                      )}
                    </>
                  )}
                  <span className="font-normal text-black/40 dark:text-white/40">
                    {p.vorname} ist hier {f.eigeneRolle === "kind" ? "Kind" : "Elternteil"}
                  </span>
                </p>
                {(["elternteil", "kind"] as const).map((rolle) => {
                  const leute = f.mitglieder.filter((m: any) => m.rolle === rolle);
                  // Leere Zeile nur zeigen, wenn dort auch etwas einzutragen
                  // ist - sonst fehlt genau da der Knopf, wo er hingehört.
                  if (leute.length === 0 && !darfBearbeiten) return null;

                  /*
                   * Zwei Rechte, zwei Knöpfe — und sie fallen auseinander:
                   * eine BESTEHENDE Person einzuhängen verbindet zwei
                   * Zweige (`darfVerknuepfen`), eine NEUE anzulegen hängt
                   * nur ein frisches Blatt an (`darfErweitern`). Am
                   * Altbestand ohne Ersteller ist regelmäßig das zweite
                   * erlaubt und das erste nicht.
                   */
                  // Nicht `leute.length`: das sind nur die sichtbaren.
                  const voll = rolle === "elternteil" && (f.elternAnzahl ?? leute.length) >= 2;
                  const art: Verwandtschaftsart | null = f.eigeneRolle
                    ? artFuerZeile(f.eigeneRolle, rolle)
                    : null;
                  const kannNeu = Boolean(f.darfErweitern) && art !== null;
                  const kannVorhandene = Boolean(f.darfVerknuepfen);
                  const offen =
                    einhaengen !== null &&
                    einhaengen.familieId === f.id &&
                    einhaengen.rolle === rolle;

                  return (
                    <div key={rolle} className="mb-1 flex flex-wrap items-baseline gap-2 text-sm">
                      <span className="w-20 shrink-0 text-black/45 dark:text-white/45">
                        {rolle === "elternteil" ? "Eltern" : "Kinder"}
                      </span>
                      {leute.map((m: any) => (
                        <span key={m.person.id} className="flex items-center gap-1">
                          {m.person.id === personId ? (
                            <span className="font-medium">
                              {m.person.vorname} {m.person.famname}
                            </span>
                          ) : (
                            <Link
                              to={`/person/${m.person.id}`}
                              {...karte.fuer(fuerKarte(m.person), {
                                umfeld: umfeld.get(m.person.id),
                              })}
                              className="hover:underline"
                            >
                              {m.person.vorname} {m.person.famname}
                            </Link>
                          )}
                          {/* Lösen ist eine Kantenänderung: `darfVerknuepfen`,
                              nicht `darfBearbeiten`. Am Altbestand (ohne
                              Ersteller) gäbe dieser Knopf sonst nur 403. */}
                          {m.darfVerknuepfen && (
                            <button
                              title="aus dieser Familie lösen"
                              onClick={() => loesen(f.id, m.person.id)}
                              className="text-black/25 hover:text-red-700 dark:text-white/25"
                            >
                              ×
                            </button>
                          )}
                        </span>
                      ))}

                      {/* Zwei Elternteile sind das Maximum - dann kein Knopf. */}
                      {!voll &&
                        (kannVorhandene || kannNeu) &&
                        (offen && kannVorhandene ? (
                          <PersonSucher
                            onWahl={(person) => mitgliedHinzufuegen(f.id, rolle, person)}
                            onAbbrechen={() => setEinhaengen(null)}
                            hinweis={
                              <>
                                Name eintippen, gern mehrteilig („Huber Hans
                                1887“). Wer noch nicht im Stammbaum steht, wird
                                unten angelegt.
                              </>
                            }
                            unten={
                              fehler || (kannNeu && art) ? (
                                <>
                                  {/* Hier, nicht nur oben auf der Seite: wer in
                                      der Lade eine Person wählt, die er nicht
                                      verbinden darf, schaut in die Lade. */}
                                  {fehler && (
                                    <p className="px-2 py-1 text-xs text-red-700">{fehler}</p>
                                  )}
                                  {kannNeu && art && (
                                    <button
                                      onClick={() => neuVerwandten(f.id, art)}
                                      className="w-full rounded px-2 py-1 text-left text-sm
                                                 hover:bg-black/5 dark:hover:bg-white/10"
                                    >
                                      + {VERWANDTSCHAFT_LABEL[art]} neu anlegen
                                    </button>
                                  )}
                                </>
                              ) : null
                            }
                          />
                        ) : (
                          <button
                            title={
                              kannVorhandene
                                ? "vorhandene Person wählen oder eine neue anlegen"
                                : "neue Person anlegen"
                            }
                            onClick={() => {
                              setFehler(null);
                              // Ohne das Recht an bestehenden Personen ist die
                              // Suche sinnlos - dann gleich das Formular.
                              if (kannVorhandene) setEinhaengen({ familieId: f.id, rolle });
                              else if (art) neuVerwandten(f.id, art);
                            }}
                            className="rounded border border-black/15 px-1.5 text-xs text-black/50
                                       hover:border-black/40 hover:text-black
                                       dark:border-white/15 dark:text-white/50"
                          >
                            + {art ? VERWANDTSCHAFT_LABEL[art] : "vorhandene"}
                          </button>
                        ))}
                    </div>
                  );
                })}

                {f.medien?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {f.medien.map((m: any) => (
                      <Link
                        key={m.id}
                        to={`/foto/${m.id}`}
                        title={`${istPdf(m.mimetyp) ? "Dokument" : "Bild"} der Familie: ${m.dateiname}`}
                        className="relative block h-16 w-16 overflow-hidden rounded border border-black/10
                                   hover:border-black/40 dark:border-white/10"
                      >
                        <img src={bildUrl(m.id, 160)} alt="" className="h-full w-full object-cover" />
                        {istPdf(m.mimetyp) && (
                          <span className="absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1
                                           text-[9px] font-medium uppercase text-white">
                            pdf
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/*
            Die Knoepfe in den Familienzeilen decken nur die Rollen ab,
            die die Person schon hat. Wer nirgends Kind ist, hat keine
            Elternzeile - und braucht trotzdem einen Weg zu Vater und
            Geschwistern. Dafuer entsteht eine neue Familie.
          */}
          {darfBearbeiten && fehlendeArten.length > 0 && (
            <div className={familien.length === 0 ? "" : "mt-4"}>
              <p className="text-sm text-black/50">
                {familien.length === 0
                  ? "Noch keiner Familie zugeordnet. Der erste Verwandte legt sie an:"
                  : "Fehlt jemand? Dafür wird eine neue Familie angelegt:"}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {fehlendeArten.map((a) => (
                  <button
                    key={a}
                    onClick={() => neuVerwandten(null, a)}
                    className="rounded-lg border border-black/15 px-3 py-1.5 text-sm
                               hover:border-black/40 dark:border-white/15"
                  >
                    + {VERWANDTSCHAFT_LABEL[a]}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <section className="mt-8">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-black/50">
            Ereignisse ({detail.data.ereignisse?.length ?? 0})
          </h2>
          {darfBearbeiten && !ereignisWerte && (
            <button
              onClick={() => {
                setFehler(null);
                setEreignisWerte(
                  neuesEreignis({ id: personId, vorname: p.vorname, famname: p.famname }),
                );
              }}
              className="rounded-lg border border-black/15 px-3 py-1.5 text-sm
                         hover:border-black/40 dark:border-white/15"
            >
              + Ereignis
            </button>
          )}
        </div>

        {ereignisWerte && (
          <div className="mb-4">
            <EreignisFormular
              key={ereignisWerte.id ?? "neu"}
              start={ereignisWerte}
              person={{ id: personId, vorname: p.vorname, famname: p.famname }}
              familien={familien}
              onFertig={() => {
                setEreignisWerte(null);
                neuLaden();
              }}
              onAbbrechen={() => setEreignisWerte(null)}
            />
          </div>
        )}

        {detail.data.ereignisse?.length ? (
          <ul className="space-y-1 text-sm">
            {detail.data.ereignisse.map((e: any) => {
              // Die anderen Beteiligten - bei einer Hochzeit steht so der
              // Partner in der Zeile, statt zweimal nur "Hochzeit".
              // Nach Rolle gruppiert: "mit Anna · Trauzeugen Hans, Eva" statt
              // alle drei hinter einem "mit" - das las sich, als wäre die
              // Trauzeugin mitgeheiratet worden.
              const offen = offeneTexte.has(e.id);
              const andere = (e.beteiligte ?? []).filter((b: any) => b.id !== personId);
              const gruppen = ROLLENFOLGE.map((rolle) => ({
                rolle,
                leute: andere.filter((b: any) => b.rolle === rolle),
              })).filter((g) => g.leute.length > 0);

              return (
                <li key={e.id} className="group">
                  <div className="flex items-baseline gap-3">
                    <span className="w-40 shrink-0 text-black/50 dark:text-white/50">
                      {e.datumVon || e.datumText
                        ? formatDatum({
                            von: e.datumVon,
                            bis: e.datumBis,
                            genauigkeit: e.datumGenauigkeit ?? "jahr",
                            text: e.datumText,
                          })
                        : "—"}
                    </span>
                    <span className="flex-1">
                      {EREIGNIS_LABEL[e.art as keyof typeof EREIGNIS_LABEL] ?? e.art}
                      {gruppen.map((g, gi) => (
                        <span key={g.rolle} className="text-black/50 dark:text-white/50">
                          {gi === 0 ? " " : " · "}
                          {ereignisrolleText(g.rolle, g.leute.length, g.leute[0]?.geschlecht)}{" "}
                          {/*
                            Die Karte nur, wo es etwas zu zeigen gibt: ein
                            Beteiligter ausserhalb der Verwandtschaft bringt
                            nur seinen Namen mit, und eine Karte mit bloss
                            dem Namen ist Lärm.
                          */}
                          {g.leute.map((b: any, i: number) => (
                            <span key={b.id}>
                              {i > 0 && ", "}
                              <Link
                                to={`/person/${b.id}`}
                                {...(baumPersonen.has(b.id)
                                  ? karte.fuer(fuerKarte(b), { umfeld: umfeld.get(b.id) })
                                  : {})}
                                className="hover:underline"
                              >
                                {b.vorname} {b.famname}
                              </Link>
                            </span>
                          ))}
                        </span>
                      ))}
                      {e.ortName && (
                        <span className="text-black/50 dark:text-white/50">
                          {gruppen.length > 0 ? " · in " : " in "}
                          {e.ortName}
                        </span>
                      )}
                      {e.beschreibung && (
                        <button
                          onClick={() => textUmschalten(e.id)}
                          aria-expanded={offen}
                          // Zugeklappt steht der Anfang im Hinweistext:
                          // so sieht man mit der Maus, worum es geht,
                          // ohne dass die Zeile laenger wird.
                          title={
                            offen
                              ? "Anmerkung zuklappen"
                              : markdownErsteZeile(e.beschreibung, 120)
                          }
                          className="ml-2 rounded px-1 align-middle text-black/35
                                     hover:bg-black/5 hover:text-black dark:text-white/35
                                     dark:hover:bg-white/10 dark:hover:text-white"
                        >
                          ☰
                        </button>
                      )}

                      {e.verweise?.length > 0 && (
                        <span className="text-black/50 dark:text-white/50">
                          {e.verweise
                            // Nur http(s) - was anderes kommt zwar nicht durch
                            // die API, aber ein Link ist der falsche Ort, um
                            // sich darauf zu verlassen.
                            .filter((v: any) => /^https?:\/\//i.test(v.url ?? ""))
                            .map((v: any) => (
                              <span key={v.id}>
                                {" · "}
                                <a
                                  href={v.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={v.url}
                                  className="underline hover:text-black dark:hover:text-white"
                                >
                                  {v.titel}
                                </a>
                                <span aria-hidden> ↗</span>
                              </span>
                            ))}
                        </span>
                      )}

                      {e.medien?.length > 0 && (
                        <span className="mt-1 flex flex-wrap gap-1.5">
                          {e.medien.map((m: any) => (
                            <Link
                              key={m.id}
                              to={`/foto/${m.id}`}
                              title={
                                `${istPdf(m.mimetyp) ? "Dokument" : "Bild"} zum Ereignis: ${m.dateiname}` +
                                (m.hatText ? " — mit Transkription" : "")
                              }
                              className="relative block h-16 w-16 overflow-hidden rounded border border-black/10
                                         hover:border-black/40 dark:border-white/10"
                            >
                              <img
                                src={bildUrl(m.id, 160)}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                              {istPdf(m.mimetyp) && (
                                <span className="absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1
                                                 text-[9px] font-medium uppercase text-white">
                                  pdf
                                </span>
                              )}
                              {/*
                                Die Abschrift liegt beim Bild, nicht beim
                                Ereignis. Ohne dieses Zeichen sieht man von
                                hier aus nicht, dass es sie gibt.
                              */}
                              {m.hatText && (
                                <span className="absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1
                                                 text-[9px] leading-tight text-white">
                                  ☰
                                </span>
                              )}
                            </Link>
                          ))}
                        </span>
                      )}
                    </span>
                    {darfBearbeiten && (
                      <span className="invisible flex gap-2 group-hover:visible">
                        <button
                          onClick={() => {
                            setFehler(null);
                            setEreignisWerte(ereignisAlsWerte(e));
                          }}
                          title="bearbeiten"
                          className="text-black/40 hover:text-black dark:text-white/40 dark:hover:text-white"
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => ereignisLoeschen(e)}
                          title="löschen"
                          className="text-black/40 hover:text-red-700 dark:text-white/40"
                        >
                          ×
                        </button>
                      </span>
                    )}
                  </div>

                  {/*
                    Der ganze Text erst auf Klick. Die Zeile bleibt eine
                    Zeile, auch wenn darin ein ganzer Matrikeneintrag
                    steckt - sichtbar ist nur, DASS einer da ist.
                  */}
                  {offen && (
                    <div className="mt-1 rounded-lg bg-black/[0.03] p-3 text-sm
                                    dark:bg-white/5 sm:ml-[10.75rem]">
                      <Markdown text={e.beschreibung} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-black/50">
            Noch keine Ereignisse.
            {darfBearbeiten && " Taufe, Hochzeit, Tod … mit Datum, Ort und Beteiligten."}
          </p>
        )}
      </section>

      {/*
        `key`: sonst bleibt beim Wechsel auf eine andere Person der
        aufgeklappte Zustand der Gruppen stehen - die Seite bleibt ja
        montiert, nur der Parameter aendert sich.
      */}
      <Verwandtschaft
        key={personId}
        personen={baumLeute}
        kanten={baumKanten}
        umkreis={BAUM_GRAD}
        wurzel={personId}
        laedt={baum.isPending}
        fehler={Boolean(baum.error)}
      />

      <section className="mt-8">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-black/50">
            Fotos und Dokumente ({detail.data.medien?.length ?? 0})
          </h2>
        </div>

        {darfBearbeiten && (
          <div className="mb-3">
            <FotoAblage
              aktiv={!laeuft}
              zuordnen={(medienId) => api.medienVerknuepfen(medienId, "person", personId)}
              fertig={stapelFertig}
            />
          </div>
        )}

        {detail.data.medien?.length ? (
          <div className="flex flex-wrap gap-2">
            {detail.data.medien.map((m: any) => (
              <div key={m.m.id} className="group relative">
                <Link
                  to={`/foto/${m.m.id}`}
                  title={m.tagId ? "auf diesem Bild markiert" : m.m.dateiname}
                  className="block h-24 w-24 overflow-hidden rounded-lg border border-black/10
                             hover:border-black/40 dark:border-white/10"
                >
                  <img
                    src={bildUrl(m.m.id, 160, m.tagId)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </Link>
                {istPdf(m.m.mimetyp) && (
                  <span
                    title="Dokument (PDF)"
                    className="pointer-events-none absolute bottom-1 left-1 rounded bg-black/70
                               px-1 text-[10px] font-medium uppercase tracking-wide text-white"
                  >
                    pdf
                  </span>
                )}
                {portrait?.medienId === m.m.id && (
                  <span
                    title="ist das Portrait"
                    className="absolute -left-1.5 -top-1.5 flex h-6 w-6 items-center justify-center
                               rounded-full border border-amber-500 bg-amber-400 text-xs shadow"
                  >
                    ★
                  </span>
                )}
                {darfBearbeiten && !istPdf(m.m.mimetyp) && portrait?.medienId !== m.m.id && (
                  <button
                    onClick={() => alsPortrait(m.m.id, m.tagId ?? null)}
                    title="als Portrait dieser Person verwenden"
                    className="absolute -left-1.5 -top-1.5 hidden h-6 w-6 items-center justify-center
                               rounded-full border border-black/15 bg-white text-xs leading-none
                               text-black/50 shadow group-hover:flex hover:text-amber-600
                               dark:border-white/20 dark:bg-neutral-800 dark:text-white/50"
                  >
                    ☆
                  </button>
                )}
                {darfBearbeiten && (
                  <button
                    onClick={() => fotoZuordnungLoesen(m.m.id)}
                    title="gehört nicht zu dieser Person"
                    className="absolute -right-1.5 -top-1.5 hidden h-6 w-6 rounded-full border
                               border-black/15 bg-white text-sm leading-none text-black/60
                               shadow group-hover:block hover:text-red-700
                               dark:border-white/20 dark:bg-neutral-800 dark:text-white/60"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-black/50">
            Noch keine Fotos.
            {darfBearbeiten &&
              " Auf einem Gruppenfoto kannst du danach Personen markieren; Urkunden gehen als PDF."}
          </p>
        )}
      </section>

      {karte.karte}
    </div>
  );
}
